import { InputHTMLAttributes, useCallback } from 'react';
import { cn } from '@utils/cn';

interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  helperText?: string;
  integerOnly?: boolean;
}

function normalizeNumericString(raw: string, integerOnly: boolean): string {
  const normalized = raw.replace(',', '.');
  if (integerOnly) {
    const digitsOnly = normalized.replace(/[^0-9]/g, '');
    return digitsOnly;
  }
  const parts = normalized.split('.');
  if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('');
  const allowed = normalized.replace(/[^0-9.]/g, '');
  const firstDot = allowed.indexOf('.');
  if (firstDot === -1) return allowed;
  return allowed.slice(0, firstDot + 1) + allowed.slice(firstDot + 1).replace(/\./g, '');
}

export default function NumberInput({
  value,
  onChange,
  className,
  label,
  error,
  helperText,
  integerOnly = false,
  ...props
}: NumberInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') {
        onChange('');
        return;
      }
      onChange(normalizeNumericString(raw, integerOnly));
    },
    [onChange, integerOnly]
  );

  return (
    <div className="w-full">
      {label && <label className="input-label">{label}</label>}
      <input
        type="text"
        inputMode={integerOnly ? 'numeric' : 'decimal'}
        autoComplete="off"
        value={value}
        onChange={handleChange}
        className={cn(
          'input-base input-placeholder',
          error && 'input-error',
          className
        )}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {helperText && !error && <p className="mt-1 text-sm text-gray-500">{helperText}</p>}
    </div>
  );
}
